import styled from '@emotion/styled';
import { type FC, type HTMLAttributes, type PropsWithChildren } from 'react';

import { resolveColor } from '../shared/colorUtils';
import {
  headingSizeStyles,
  headingUppercase,
  textFontWeightStyles,
  textWrapStyles,
} from './Heading.styles';
import {
  type TFontWeight,
  type THeadingSize,
  type TTextAlign,
  type TTextWrap,
} from './types';

const SIZE_TO_ELEMENT_MAP = {
  xl: 'h1',
  lg: 'h2',
  md: 'h3',
  sm: 'h4',
  xs: 'h5',
  '2xs': 'h6',
};

export type HeadingProps = HTMLAttributes<HTMLElement> & {
  /**
   * Size variant of the heading
   */
  size?: THeadingSize;
  /**
   * DOM element name to render as
   */
  as?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Whether to transform text to uppercase
   */
  uppercase?: boolean;
  /**
   * Font weight of the heading
   */
  fontWeight?: TFontWeight;
  /**
   * Text alignment
   */
  textAlign?: TTextAlign;
  /**
   * Text wrapping behavior
   */
  textWrap?: TTextWrap;
  /**
   * Color token or CSS color value
   */
  color?: string;
};

const StyledHeading = styled('h3')<HeadingProps>`
  ${({ size = 'md' }) => headingSizeStyles[size]}
  ${({ uppercase }) => uppercase && headingUppercase}
  ${({ textWrap = 'pretty' }) => textWrapStyles[textWrap]}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
  ${({ color: colorProp }) => colorProp && `color: ${resolveColor(colorProp)};`}
  ${({ fontWeight = 'normal' }) => textFontWeightStyles[fontWeight]}
`;

const Heading: FC<PropsWithChildren<HeadingProps>> = ({
  as,
  size = 'md',
  children,
  className,
  uppercase,
  textAlign,
  textWrap = 'pretty',
  color = 'var(--color-text-primary)',
  fontWeight,
  ...props
}) => {
  const elementType = (as ??
    SIZE_TO_ELEMENT_MAP[size]) as keyof JSX.IntrinsicElements;

  return (
    <StyledHeading
      as={elementType}
      size={size}
      className={className}
      uppercase={uppercase}
      textAlign={textAlign}
      textWrap={textWrap}
      color={color}
      fontWeight={fontWeight}
      {...props}
    >
      {children}
    </StyledHeading>
  );
};

export default Heading;
